/**
 * One value in the run ledger, written the way its PROVENANCE demands.
 *
 * The five states are the point of the grid: a number read off a page, a
 * number the recipe computed, a number a person typed, a number nobody could
 * find, and a number that does not exist. Each gets a treatment that says
 * which without a legend — and the state is repeated in `title` so it never
 * depends on colour alone.
 *
 * The cited underline uses a slash-opacity brand colour rather than the
 * `rgba(...)` arbitrary value: Tailwind's extractor has silently dropped
 * comma-bearing arbitrary values in the production bundle before (see
 * `StatusPill`), and an underline that only exists in dev is worse than none.
 */

import { AlertTriangle } from "lucide-react";
import React from "react";

import type { MonitoringFigure } from "../../types/monitoring";
import FormulaPopover from "./FormulaPopover";
import { NO_VALUE, formatFigure } from "./runFormat";

export interface LedgerCellProps {
	figure: MonitoringFigure;
	/** Open the figure's evidence — the page the quote was read from. */
	onOpenSource: (figure: MonitoringFigure) => void;
	/** A gap is work, not a value: clicking it goes to the work list. */
	onOpenGaps: (figure: MonitoringFigure) => void;
	/**
	 * Every figure of the run, keyed. A derived cell's Σ opens the formula
	 * written in the inputs' labels with the values this run used, and neither
	 * is knowable from the one figure.
	 */
	figuresByKey?: ReadonlyMap<string, MonitoringFigure>;
}

const CITED_UNDERLINE = "border-b-[1.5px] border-[#004C4B]/35";
const UNVERIFIED_UNDERLINE =
	"underline decoration-dotted decoration-warning-fg underline-offset-4";

const LedgerCell: React.FC<LedgerCellProps> = ({
	figure,
	onOpenSource,
	onOpenGaps,
	figuresByKey,
}) => {
	const text = formatFigure(figure.value, figure.unit);

	if (figure.state === "gap") {
		return (
			<td className="border-b border-gray-100 bg-[#fffaeb] px-3 py-1.5 text-right">
				<button
					type="button"
					data-testid={`ledger-gap-${figure.key}`}
					onClick={() => onOpenGaps(figure)}
					title={figure.gap_reason ?? "Not in package"}
					className="inline-flex items-center gap-1 text-sm text-[#92400e] hover:underline"
				>
					<AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
					Not in package
				</button>
			</td>
		);
	}

	if (figure.state === "not_available") {
		return (
			<td
				className="border-b border-gray-100 px-3 py-1.5 text-right tabular-nums text-text-subtle"
				title={figure.note ?? "Not available"}
				data-testid={`ledger-value-${figure.key}`}
			>
				n/a
			</td>
		);
	}

	if (figure.state === "manual") {
		return (
			<td
				className="border-b border-gray-100 px-3 py-1.5 text-right tabular-nums font-medium text-primary-600"
				title={`Entered by ${figure.entered_by_name ?? "a person"}${
					figure.note ? ` · ${figure.note}` : ""
				}`}
				data-testid={`ledger-value-${figure.key}`}
			>
				{text} •
			</td>
		);
	}

	if (figure.state === "derived") {
		// A derived figure the server could not tie to the certificate is still
		// the recipe's arithmetic — it keeps its Σ — but it is no longer a number
		// to read off the page, so it takes the SAME dotted amber as a quote that
		// could not be re-read. The live run that produced a net leverage of
		// −0.356x looked exactly like a good number until this said otherwise.
		const untied = !figure.verified;
		return (
			<td
				className="border-b border-gray-100 px-3 py-1.5 text-right tabular-nums text-text-base"
				data-testid={`ledger-value-${figure.key}`}
			>
				{untied ? (
					<span
						className={UNVERIFIED_UNDERLINE}
						title={figure.note ?? "Does not tie to the certificate"}
						data-testid={`ledger-untied-${figure.key}`}
					>
						{text}
					</span>
				) : (
					text
				)}
				{figure.formula ? (
					// The formula is no longer printed in the Source column (DOP-460):
					// it is arithmetic to inspect, not provenance to scan past.
					<FormulaPopover figure={figure} figuresByKey={figuresByKey} />
				) : (
					<span
						className="ml-1 text-gray-400"
						title="Computed from other figures"
						data-testid={`ledger-derived-${figure.key}`}
					>
						Σ
					</span>
				)}
			</td>
		);
	}

	// found — the value is cited to a page, so the number itself is the link.
	const clickable = Boolean(figure.source?.anchor);
	return (
		<td
			className="border-b border-gray-100 px-3 py-1.5 text-right tabular-nums text-text-base"
			data-testid={`ledger-cell-${figure.key}`}
		>
			{figure.value === null ? (
				<span className="text-text-subtle">{NO_VALUE}</span>
			) : clickable ? (
				<button
					type="button"
					data-testid={`ledger-value-${figure.key}`}
					onClick={() => onOpenSource(figure)}
					title={
						figure.verified
							? `${figure.source?.filename ?? "Source"}${
									figure.source?.page ? ` · p.${figure.source.page}` : ""
								}`
							: "Quote not re-read on the page"
					}
					className={`tabular-nums ${
						figure.verified ? CITED_UNDERLINE : UNVERIFIED_UNDERLINE
					}`}
				>
					{text}
				</button>
			) : (
				<span
					data-testid={`ledger-value-${figure.key}`}
					title={figure.verified ? undefined : "Quote not re-read on the page"}
					className={figure.verified ? undefined : UNVERIFIED_UNDERLINE}
				>
					{text}
				</span>
			)}
		</td>
	);
};

export default LedgerCell;
